import React, { useEffect, useState } from 'react';

import CircularProgress from '@material-ui/core/CircularProgress';
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';

import theme from 'constants/theme';

import { universalSearchAutoCompleteObjectives } from 'services/search';

import { Typography4 } from 'uiToolkit/Typography';

import { StyledTextField } from './styles';

const lowerCaseExists = (options = [], inputString = '') => options.some((option) => option.label === inputString);

export default function ObjectiveNameAutoComplete({
  onInputChange: onOptionChange,
  inputValue: preSelectedOptionDisplayValue,
  selectedTimeBoundPeriod,
  disabled,
  autoFocus = false,
  //   searchString = '',
  width = '100%',
  placeholder = 'Name',
}) {
  const [open, setOpen] = useState(false);
  const preSelectedOption = preSelectedOptionDisplayValue
    ? {
        value: preSelectedOptionDisplayValue,
        label: preSelectedOptionDisplayValue,
      }
    : null;
  const [inputValue, setInputValue] = useState(preSelectedOptionDisplayValue || '');

  const initialOptions = preSelectedOption ? [preSelectedOption] : [];
  const [options, setOptions] = useState(initialOptions);
  const [loading, setLoading] = useState(false);
  //   const loading = open && options.length === 0;

  const loadOptions = (text) =>
    universalSearchAutoCompleteObjectives({
      text,
      tagsSet: [
        {
          tagType: 'GOAL_TAG',
          limit: 10,
        },
      ],
      timePeriod: selectedTimeBoundPeriod,
    });

  useEffect(() => {
    let active = true;

    if (!open || !inputValue) {
      return undefined;
    }

    (async () => {
      setLoading(true);
      const response = await loadOptions(inputValue);

      if (active) {
        setOptions([...initialOptions, ...response]);
        setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [inputValue]);

  useEffect(() => {
    if (!open) {
      setOptions(initialOptions);
    }
    if (disabled) {
      setInputValue(preSelectedOptionDisplayValue);
    }
  }, [open, disabled]);

  //  const handleOptionChange = props => {
  //     const { value, __isNew__ } = props;
  //     if (__isNew__) {
  //       onOptionChange(value, null, true);
  //     } else {
  //       const { objectiveId, objectiveType } = props;
  //       onOptionChange(value, { objectiveId, objectiveType });
  //     }
  //   };

  const filter = createFilterOptions();

  return (
    <Autocomplete
      disabled={disabled}
      id="async-objective-autocomplete"
      style={{
        justifyContent: 'center',
        width,
      }}
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      getOptionSelected={(option, value) => option.name === value.name}
      getOptionLabel={(option) => option.label}
      getOptionDisabled={(option) => option.disabled}
      options={options}
      loading={loading}
      size="small"
      value={preSelectedOption}
      freeSolo
      filterOptions={(options, params) => {
        let filtered = filter(options, params);
        if (filtered?.length) {
          filtered = [
            {
              label: 'Add similar objectives from other time periods',
              value: null,
              disabled: true,
            },
            ...filtered,
          ];
        }
        // Suggest the creation of a new value
        // if (params.inputValue !== '' && !lowerCaseExists(options, params.inputValue)) {
        //   const createNewOption = {
        //     value: params.inputValue,
        //     label: `Create New Objective" ${params.inputValue}"`
        //   };
        //   filtered = [createNewOption, ...filtered];
        // }

        return filtered;
      }}
      //   renderOption={option => option.label}
      renderOption={(option, { inputValue }) => {
        if (option.disabled) {
          return (
            <Typography4 display="inline" component="span" customColor={theme.palette.text.heading}>
              {option.label}
            </Typography4>
          );
        }
        const matches = match(option.label, inputValue);
        const parts = parse(option.label, matches);
        return (
          <div>
            {parts.map((part, index) => (
              <Typography4
                display="inline"
                component="span"
                customColor={part.highlight ? theme.palette.text.label : theme.palette.text.heading}
              >
                {part.text}
              </Typography4>
            ))}
          </div>
        );
      }}
      inputValue={inputValue}
      onChange={(e, textOptionValue, operationType, option) => {
        if (operationType === 'create-option') {
          onOptionChange(textOptionValue, null, true);
        } else if (operationType === 'select-option') {
          const { objectiveId, objectiveType, value } = option?.option ?? {};
          onOptionChange(value, { objectiveId, objectiveType });
        } else if (
          [
            'blur',
            // , 'clear', 'remove-option'
          ].includes(operationType)
        ) {
          setInputValue(preSelectedOptionDisplayValue);
        }
      }}
      onInputChange={(e, value) => {
        if (e) {
          if (e.keyCode === 13) {
            // // if enter key is pressed
            // // if not existing value in options, call onOptionsChange
            // if (!lowerCaseExists(options, inputValue)) {
            //   onOptionChange(inputValue, null, true);
            // }
            setInputValue(value);
          } else {
            setInputValue(value);
          }
        }
      }}
      disableClearable
      renderInput={(params) => (
        <StyledTextField
          {...params}
          placeholder={placeholder}
          variant="outlined"
          onBlur={(e) => {
            if (!lowerCaseExists(options, inputValue)) {
              onOptionChange(inputValue, null, true);
            }
          }}
          autoFocus={autoFocus}
          inputProps={{
            ...params.inputProps,
            autoComplete: 'new-password',
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
